/**
 * Which payment rails a TRX PayPage session DECLARES.
 *
 * A card session that renders wallet buttons declares `'Wallet'`, which emits
 * `{TranType:"Credit", CardType:[...brands], GooglePay:"1", ApplePay:"1"}` —
 * in that order, because TRX's XSD makes `PaymentType` a strict SEQUENCE.
 *
 * THE BLOCK MUST BE PRESENT. Omitting `PaymentTypes` entirely — letting TRX
 * fall back to the PayPage admin config — reads as the tidier design and was
 * shipped on that reasoning. It took card payments down in PRODUCTION.
 *
 * The session still mints `ResponseCode "00" Initialized`, so our side looks
 * healthy. The failure lands one step later, inside TRX's own iframe, when it
 * calls its `/api/PayPage/Init`:
 *
 *     POST https://pay.trxservices.com/api/PayPage/Init -> 500
 *     "Object reference not set to an instance of an object."
 *     (initApi.ts:18 -> useInitApp.tsx:26)
 *
 * A .NET null reference in THEIR handler, on a session carrying no
 * PaymentTypes. Both their UAT and production environments do it. So the
 * block being "optional" per §10.5 is true of the Initialize call and NOT
 * true of the PayPage Init that follows it.
 *
 * THE MEASUREMENT THAT SETTLES IT — the request body size, from our own API
 * logs, same restaurant, same PayPage Guid (EV2WDM2U00D5HEK), same account:
 *
 *     02:53:33Z  Content-Length: 138   -> 00 Initialized, wallets rendered
 *     10:59:34Z  Content-Length: 112   -> 00 Initialized, then Init 500s
 *
 * 26 bytes apart, and `,"paymentTypes":["Wallet"]` is 26 characters. Both
 * mints succeed identically; only the PayPage Init that follows differs. Note
 * what is NOT reliable here: the Vercel deployment `created` timestamp says
 * this build existed before the working test, which argues the opposite — it
 * is when the build was QUEUED, and with a saturated build queue it had not
 * served traffic yet. The payload size is the evidence; a deployment
 * timestamp is not.
 *
 * MEASURED, not reasoned. The working configuration is on the record: the
 * mint at 2026-09-02T02:53:33Z (dev, deployment dpl_AAeCfsgMg1ZG84LtrkrCQ5x9mZC5,
 * commit 17f477a81) sent `['Wallet']`, returned `00 Initialized`, and rendered
 * both wallet buttons. The next deploy omitted the block and every card form
 * since has failed. Do not remove it again without a live Init that succeeds
 * without it.
 *
 * `hasAmount` is load-bearing: the amount-less path resolves to `Avs`, which
 * `assertPayPageRailsSupportInnerAction` refuses for Wallet, and that throw
 * rejects the mint and takes the card form with it.
 *
 * Extracted as a pure function for the same reason `resolveWalletOwner` was
 * (see `apps/checkout/src/lib/walletOwner.ts`): inline in a fetch payload the
 * rule is unfalsifiable, and the suite stayed green through every one of
 * these.
 *
 * ── Why it lives in `@order-up/common` ──────────────────────────────────────
 * TWO planes mint a PayPage session and they must declare the SAME rail. The
 * browser mints one through `POST /payments/trx/paypage/init`
 * (`apps/checkout`'s `TrxJsLibraryInput`); the SERVER mints one for a guest
 * paying at the table, who holds a check token and no session at all
 * (`apps/api`'s `/checks/:checkToken/card-session`). `apps/api` cannot import
 * an app, and `core` and `client` are siblings — so a copy on either side is a
 * session that DECLARES one rail while the page MOUNTS the buttons for
 * another, which is a guest staring at a form nothing accepts. Exported from
 * BOTH `index.ts` and `node.ts` for the same reason
 * `computeGuestCheckBaseCents` is.
 */

/**
 * One rail a PayPage session may declare — `Session.PaymentTypes.PaymentType[]`
 * per §10.5.
 *
 * Declared HERE rather than imported, because this package sits below both
 * planes that need it. It is structurally identical to the two
 * `TrxPayPagePaymentType` aliases that already exist
 * (`packages/core/src/services/trx/adapters/PayPageInitialize.ts` and
 * `packages/client/src/services/trxPaymentsApi.ts`), so a value of this type
 * is assignable to either without a cast. Converging those three names is a
 * separate change and deliberately not made here.
 */
export type PayPageRail = 'Card' | 'Ach' | 'Wallet';

export interface PayPageRailInput {
    /** Card vs. ACH — the two field maps are disjoint. */
    readonly paymentType: 'card' | 'ach';
    /** Whether this surface renders wallet buttons at all. */
    readonly enableWallets: boolean;
    /** Whether the session carries an amount (selects Sale over Avs). */
    readonly hasAmount: boolean;
}

/**
 * Resolve the `paymentTypes` for a PayPage session mint. Always returns a
 * rail — never undefined. See the module header: an absent block 500s TRX's
 * PayPage Init.
 */
export function resolvePayPageRails({
    paymentType,
    enableWallets,
    hasAmount,
}: PayPageRailInput): readonly PayPageRail[] {
    if (paymentType === 'ach') return ['Ach'];
    return enableWallets && hasAmount ? ['Wallet'] : ['Card'];
}
